import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import AddCourseDialog from './AddCourseDialog';
import EditCourseDialog from './EditCourseDialog';
import './Courses.css';

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [faculties, setFaculties] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    faculty_id: '',
    semester: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [totalCourses, setTotalCourses] = useState(0);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const fetchCourses = React.useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Build query parameters from filters only
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          if (key === 'semester') {
            // Try multiple case variations for semester
            const semesterValue = value.trim();
            queryParams.append('semester', semesterValue);
            queryParams.append('semester', semesterValue.toLowerCase());
            queryParams.append('semester', semesterValue.toUpperCase());
            queryParams.append('semester', semesterValue.charAt(0).toUpperCase() + semesterValue.slice(1));
            // Also try different field names
            queryParams.append('semester_name', semesterValue);
            queryParams.append('semester_name_en', semesterValue);
          } else if (key === 'faculty_id') {
            // Try different field names for faculty
            queryParams.append('faculty_id', value.trim());
            queryParams.append('faculty_ref', value.trim());
            queryParams.append('faculty', value.trim());
          } else {
            queryParams.append(key, value.trim());
          }
        }
      });

      const response = await api.get(`/courses?${queryParams.toString()}`);
      if (response.data.success) {
        setCourses(response.data.data);
        setTotalCourses(response.data.total || response.data.data.length);
      } else {
        setCourses([]);
        setTotalCourses(0);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Unauthorized. Please log in again.');
      } else {
        setError('Error loading courses. Please try again.');
      }
      console.error('Error fetching courses:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchFaculties = React.useCallback(async () => {
    try {
      const response = await api.get('/faculties');
      if (response.data.success) {
        setFaculties(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch faculties:', err);
    }
  }, []);

  useEffect(() => {
    fetchCourses();
    fetchFaculties();
  }, [fetchCourses, fetchFaculties]); // Include dependencies

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      // Search by course code or name
      fetchCoursesWithSearch(searchTerm.trim());
    }
  };

  const fetchCoursesWithSearch = async (searchTerm) => {
    try {
      setLoading(true);
      setError('');

      // Build query parameters from filters and search
      const queryParams = new URLSearchParams();
      
      // Add filter parameters
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          if (key === 'semester') {
            // Try multiple case variations for semester
            const semesterValue = value.trim();
            queryParams.append('semester', semesterValue);
            queryParams.append('semester', semesterValue.toLowerCase());
            queryParams.append('semester', semesterValue.toUpperCase());
            queryParams.append('semester', semesterValue.charAt(0).toUpperCase() + semesterValue.slice(1));
            // Also try different field names
            queryParams.append('semester_name', semesterValue);
            queryParams.append('semester_name_en', semesterValue);
          } else if (key === 'faculty_id') {
            // Try different field names for faculty
            queryParams.append('faculty_id', value.trim());
            queryParams.append('faculty_ref', value.trim());
            queryParams.append('faculty', value.trim());
          } else {
            queryParams.append(key, value.trim());
          }
        }
      });

      // Add search parameters
      queryParams.append('course_code', searchTerm);
      queryParams.append('course_name', searchTerm);

      const response = await api.get(`/courses?${queryParams.toString()}`);
      if (response.data.success) {
        setCourses(response.data.data);
        setTotalCourses(response.data.total || response.data.data.length);
      } else {
        setCourses([]);
        setTotalCourses(0);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Unauthorized. Please log in again.');
      } else {
        setError('Error loading courses. Please try again.');
      }
      console.error('Error searching courses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      return {
        ...prev,
        [key]: value
      };
    });
    // Don't auto-fetch here, let user click "Apply Filters"
  };

  const clearFilters = () => {
    setFilters({
      faculty_id: '',
      semester: ''
    });
    setSearchTerm('');
    // Reset to show all courses
    fetchCourses();
  };

  // Course CRUD handlers
  const handleAddClick = () => {
    setIsAddDialogOpen(true);
  };

  const handleAddClose = () => {
    setIsAddDialogOpen(false);
  };

  const handleAddSave = (newCourse) => {
    setCourses(prev => [newCourse, ...prev]);
    setTotalCourses(prev => prev + 1);
  };

  const handleEditClick = (course) => {
    setEditingCourse(course);
    setIsEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setIsEditDialogOpen(false);
    setEditingCourse(null);
  };

  const handleEditSave = (updatedCourse) => {
    setCourses(prev =>
      prev.map(course =>
        course._id === updatedCourse._id ? updatedCourse : course
      )
    );
  };

  const handleDeleteCourse = async (courseId) => {
    if (window.confirm('Are you sure you want to delete this course?')) {
      try {
        const response = await api.delete(`/courses/${courseId}`);
        if (response.data.success) {
          setCourses(prev => prev.filter(course => course._id !== courseId));
          setTotalCourses(prev => prev - 1);
        } else {
          alert('Failed to delete course: ' + response.data.message);
        }
      } catch (err) {
        alert('Error deleting course: ' + (err.response?.data?.message || err.message));
      }
    }
  };



  if (loading) {
    return (
      <div className="courses-loading">
        <div className="loading-spinner"></div>
        <p>Loading courses...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="courses-error">
        <div className="error-content">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={fetchCourses} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="courses">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1>All Courses</h1>
            <p>{totalCourses} course{totalCourses !== 1 ? 's' : ''} found</p>
          </div>
          <button className="add-course-button" onClick={handleAddClick}>
            <span className="button-icon">+</span> Add New Course
          </button>
        </div>
      </div>

      <div className="search-filters-section">
        <div className="search-bar">
          <form onSubmit={handleSearch} className="search-form">
            <div className="search-input-group">
              <input
                type="text"
                placeholder="Search by course code or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <button type="submit" className="search-button">
                <span className="search-icon">🔍</span>
                Search
              </button>
            </div>
          </form>
          
          <button 
            className={`filter-toggle ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <span className="filter-icon">⚙️</span>
            {showFilters ? 'Hide' : 'Show'} Filters
          </button>
        </div>

        {showFilters && (
          <div className="filters-panel">
            <div className="filters-grid">
              <div className="filter-group">
                <label htmlFor="faculty_id">Faculty</label>
                <select
                  id="faculty_id"
                  value={filters.faculty_id}
                  onChange={(e) => handleFilterChange('faculty_id', e.target.value)}
                >
                  <option value="">All Faculties</option>
                  {faculties.map((faculty) => (
                    <option key={faculty._id} value={faculty._id}>
                      {faculty.nameEn}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="semester">Semester</label>
                <select
                  id="semester"
                  value={filters.semester}
                  onChange={(e) => handleFilterChange('semester', e.target.value)}
                >
                  <option value="">All Semesters</option>
                  <option value="1st semester">1st semester</option>
                  <option value="2nd semester">2nd semester</option>
                  <option value="3rd semester">3rd semester</option>
                  <option value="4th semester">4th semester</option>
                  <option value="5th semester">5th semester</option>
                  <option value="6th semester">6th semester</option>
                  <option value="7th semester">7th semester</option>
                  <option value="8th semester">8th semester</option>
                </select>
              </div>
            </div>

            <div className="filter-actions">
              <button onClick={clearFilters} className="clear-filters-btn">
                Clear All Filters
              </button>
              <button onClick={fetchCourses} className="apply-filters-btn">
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="courses-section">
        {courses.length > 0 ? (
          <div className="courses-grid">
            {courses.map((course) => (
              <div key={course._id} className="course-card">
                <div className="course-header">
                  <div className="course-index">
                    <span className="index-badge">{course.index}</span>
                  </div>
                  <div className="course-actions">
                    <button 
                      className="edit-course-btn" 
                      onClick={() => handleEditClick(course)}
                    >
                      Edit
                    </button>
                    <button 
                      className="delete-course-btn" 
                      onClick={() => handleDeleteCourse(course._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                
                <div className="course-content">
                  <h3 className="course-name">{course.course_name}</h3>
                  <p className="course-code">{course.course_code}</p>
                  <p className="course-credit"><strong>Credit:</strong> {course.credit}</p>
                  <p className="course-faculty"><strong>Faculty:</strong> {course.faculty_name}</p>
                  <p className="course-semester"><strong>Semester:</strong> {course.semester}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-courses">
            <div className="empty-state">
              <div className="empty-icon">📚</div>
              <h3>No Courses Found</h3>
              <p>Try adjusting your filters or add a new course.</p>
              <button onClick={handleAddClick} className="add-course-btn">
                Add First Course
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add Course Dialog */}
      <AddCourseDialog
        isOpen={isAddDialogOpen}
        onClose={handleAddClose}
        onSave={handleAddSave}
        faculties={faculties}
      />

      {/* Edit Course Dialog */}
      <EditCourseDialog
        course={editingCourse}
        isOpen={isEditDialogOpen}
        onClose={handleEditClose}
        onSave={handleEditSave}
        faculties={faculties}
      />
    </div>
  );
};

export default Courses;
